import { PreviewOverlay } from "components/PreviewOverlay"
import { TintViewer } from "components/TintViewer"
import { type FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ProductV2 } from "types"
import { Expand } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { fractionalRgbToHex, hexToRgbString } from "../ColorItem/helpers"
import { MODEL_IMAGES } from "../PreviewOverlay/types"
import { ProductCategories } from "components/Navbar/types"

interface PreviewProps {
  productItem: ProductV2
  type: ProductCategories
  disabled?: boolean
  setActiveColorIndex?: (ind: number) => void
  activeColorIndex?: number
}

export const Preview: FC<PreviewProps> = ({
  productItem,
  type = ProductCategories.MAKEUP,
  disabled,
  setActiveColorIndex,
  activeColorIndex,
}) => {
  const { t } = useTranslation()
  const [previewOverlay, setPreviewOverlay] = useState(false)
  const [image, setImage] = useState(MODEL_IMAGES[0])
  const [color, setColor] = useState<string>()

  useEffect(() => {
    if (
      type === ProductCategories.MAKEUP &&
      activeColorIndex !== undefined &&
      activeColorIndex !== null &&
      productItem?.colors[activeColorIndex]
    )
      setColor(productItem.colors[activeColorIndex].color)
  }, [productItem, activeColorIndex, type])

  return (
    <div className="space-y-3">
      {previewOverlay && (
        <PreviewOverlay
          effectType={type}
          productItem={productItem}
          image={image}
          onChangeImage={setImage}
          activeColorIndex={activeColorIndex}
          setActiveColorIndex={setActiveColorIndex}
          onClose={() => setPreviewOverlay(false)}
        />
      )}
      <div className="relative overflow-hidden rounded-xl">
        {previewOverlay || disabled ? (
          <div className="flex aspect-square w-full items-center justify-center rounded-xl border border-dashed bg-muted/20">
            {disabled && (
              <p className="px-4 text-center text-sm text-muted-foreground">
                {t("previewVTO.fillVirtualTryOnInfo")}
              </p>
            )}
          </div>
        ) : (
          <>
            <Button
              variant="outline"
              size="icon"
              className="absolute right-3 top-3 z-10 size-8 bg-background/95"
              onClick={() => setPreviewOverlay(true)}
              aria-label={t("previewVTO.expand")}
            >
              <Expand className="size-4" />
            </Button>
            <div className="aspect-square w-full">
              <TintViewer
                productItem={productItem}
                activeColor={color}
                image={image}
                effectType={type}
              />
            </div>
          </>
        )}
      </div>
      {productItem?.colors?.length > 1 && type === ProductCategories.MAKEUP ? (
        <Select
          value={String(activeColorIndex)}
          onValueChange={(value) => setActiveColorIndex?.(Number(value))}
        >
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {productItem.colors.map((to, idx) => (
              <SelectItem key={idx} value={String(idx)}>
                <div className="flex items-center gap-2.5">
                  <div
                    className="h-5 w-5 shrink-0 rounded border"
                    style={{
                      backgroundColor: fractionalRgbToHex(
                        to.color[0] === "#" ? hexToRgbString(to.color) : to.color
                      ),
                    }}
                  />
                  <span className="text-sm">
                    {to.name?.trim() || t("productPreview.tone", { color: to.color })}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
    </div>
  )
}
